<template>
  <VStepper
    :steps="[
      { label: trans('core.details'), id: 'details', hasErrors: Object.keys(detailErrors).length },
      { label: trans('core.filters'), id: 'filters', hasErrors: Object.keys(filterErrors).length },
      { label: trans('core.message'), id: 'content', hasErrors: Object.keys(contentErrors).length },
    ]"
  >
    <template #details="{ changeStep }">
      <Loader v-if="isLoading" />
      <SystemMessageDetailsEdit
        v-else
        v-model="details"
        :is-saving="isSaving"
        :errors="detailErrors"
        @change-step="changeStep"
      />
    </template>

    <template #filters="{ changeStep }">
      <SystemMessageFiltersEdit
        v-model="filters"
        :edit-data="filterEditData"
        :userable="location_userable[details.location] ?? 'none'"
        :is-saving="isSaving"
        :errors="filterErrors"
        @change-step="changeStep"
      />
    </template>

    <template #content>
      <SystemMessageContentEdit
        v-model="content"
        :is-saving="isSaving"
        :errors="contentErrors"
        @save="save"
      />
    </template>
  </VStepper>
</template>

<script>
import SystemMessageDetailsEdit from '@/employees/components/SystemMessage/view/SystemMessageDetailsEdit.vue'
import SystemMessageFiltersEdit from '@/employees/components/SystemMessage/view/SystemMessageFiltersEdit.vue'
import SystemMessageContentEdit from '@/employees/components/SystemMessage/view/SystemMessageContentEdit.vue'
import { defaultErrorMessage } from '@/helpers'

export default {
  name: 'CreateOrUpdateSystemMessage',
  components: { SystemMessageContentEdit, SystemMessageFiltersEdit, SystemMessageDetailsEdit },
  props: {
    systemMessageId: {
      type: [String, Number],
      required: false,
      default: () => null,
    },
  },
  data () {
    return {
      details: {},
      filters: {
        programs: {
          enabled: false,
          params: {},
        },
        has_photo: {
          enabled: false,
          params: {},
        },
        in_groups: {
          enabled: false,
          params: {},
        },
        student_does_not_have_characteristic: {
          enabled: false,
          params: {},
        },
      },
      content: {},

      isLoading: false,
      filterEditData: null,
      isSaving: false,
      errors: [],
      location_userable: {
        student_dashboard: 'student',
        employee_dashboard: 'employee',
        login: 'none',
      },
    }
  },

  computed: {
    detailErrors () {
      const errors = {}
      for (const errorKey in this.errors) {
        if (['starttime', 'endtime', 'location', 'style'].includes(errorKey)) {
          errors[errorKey] = this.errors[errorKey]
        }
      }

      return errors
    },
    filterErrors () {
      const errors = {}
      for (const errorKey in this.errors) {
        if (errorKey.startsWith('filters')) {
          errors[errorKey] = this.errors[errorKey]
        }
      }

      return errors
    },
    contentErrors () {
      const errors = {}
      for (const errorKey in this.errors) {
        if (['content_en', 'content_nl'].includes(errorKey)) {
          errors[errorKey] = this.errors[errorKey]
        }
      }

      return errors
    },
    readFilters () {
      const filters = {}
      for (const filterKey in this.filters) {
        if (!this.filters[filterKey].enabled) {
          continue
        }

        // only keep the id from the multi-selects
        const params = {}
        for (const key in this.filters[filterKey].params) {
          if (filterKey === 'programs' && key === 'programs') {
            params[key] = this.filters[filterKey].params[key].map(item => item.id)
          } else if (filterKey === 'in_groups' && key === 'groups') {
            params[key] = this.filters[filterKey].params[key].map(item => item.id)
          } else if (filterKey === 'student_does_not_have_characteristic' && key === 'student_characteristic') {
            params[key] = this.filters[filterKey].params[key].map(item => item.id)[0]
          } else {
            params[key] = this.filters[filterKey].params[key]
          }
        }

        filters[filterKey] = params
      }

      return filters
    },
  },

  mounted () {
    if (this.systemMessageId == null) {
      return
    }

    this.isLoading = true
    this.$store.dispatch('getSystemMessageEditData', { systemMessageId: this.systemMessageId })
      .then(response => {
        const systemMessage = response.data
        this.filterEditData = systemMessage.filterEditData

        // load the fields from the systemMessage into the different steps
        const detailKeys = ['starttime', 'endtime', 'location', 'style']
        const contentKeys = ['content_nl', 'content_en']

        // details step
        detailKeys.forEach(key => {
          this.details[key] = systemMessage[key]
        })

        // filters step in custom format
        for (const filterKey in systemMessage.filters) {
          // skip invalid filters
          if (this.filters[filterKey] == null) {
            continue
          }

          // set the params
          this.filters[filterKey].enabled = true

          const params = systemMessage.filters[filterKey]
          this.filters[filterKey].params = systemMessage.filters[filterKey]

          for (const key in systemMessage.filters[filterKey]) {
            if (this.filterEditData != null && this.filterEditData[filterKey] != null && this.filterEditData[filterKey][key] != null) {
              params[key] = this.filterEditData[filterKey][key]
            }
          }

          this.filters[filterKey].params = params
        }

        // content step
        contentKeys.forEach(key => {
          this.content[key] = systemMessage[key]
        })
      })
      .finally(() => {
        this.isLoading = false
      })
  },

  methods: {
    save () {
      this.isSaving = true
      this.errors = []
      const promise = this.systemMessageId == null ? this.create() : this.update()

      promise.then(data => {
        window.location.href = data.data.redirect
      }).catch(error => {
        this.errors = error.response.data.data
        this.isSaving = false

        defaultErrorMessage(error)
      })
    },

    create () {
      return this.$store.dispatch('createSystemMessage', {
        data: {
          ...this.details,
          filters: this.readFilters,
          ...this.content,
        },
      })
    },

    update () {
      return this.$store.dispatch('updateSystemMessage', {
        systemMessageId: this.systemMessageId,
        data: {
          ...this.details,
          filters: this.readFilters,
          ...this.content,
        },
      })
    },
  },
}
</script>
